import { useMemo, useState } from 'react';
import { Aviso } from '@/components/ui/aviso';
import { Button } from '@/components/ui/button';
import { EstadoError } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import { TODOS_LOS_DISPAROS } from '../../lib/corridas';
import type { EventoDeLaLinea } from '../../types/corridas.types';
import type { DestinoHoy, FiltroBandeja } from '../../types/price-proposals.types';
import { diaDeBolivia, rangoDelAtajo, CUALQUIER_FECHA } from '../../lib/rango-de-fechas';
import { FiltrosDeCorridas, type FiltroDeCorridas } from './filtros-de-corridas';
import { NumerosDelPeriodo } from './numeros-del-periodo';
import { AvisoAntesDelRegistro, LineaDeTiempo } from './linea-de-tiempo';
import { ProductosDeLaCorridaDialog } from './productos-de-la-corrida-dialog';
import { useLineaDeTiempo } from './use-linea-de-tiempo';

/** El filtro con el que arranca «Por fechas»: los últimos 30 días, todos los disparos. */
export function filtroInicialPorFechas(): FiltroDeCorridas {
  return {
    estrategia: null,
    periodo: rangoDelAtajo('30', diaDeBolivia()) ?? CUALQUIER_FECHA,
    disparos: TODOS_LOS_DISPAROS,
  };
}

/**
 * «Por fechas» (spec 275, R2, punto 3): la línea de tiempo de todas las
 * corridas de la empresa y de los recálculos de reglas, dentro de Estrategias.
 *
 * Ronda de arreglos (revisión T10/T11): el filtro lo puede guardar quien la
 * muestra (`filtro` + `onCambiarFiltro`), así cambiar de vista y volver no lo
 * pierde. Sin ellos, lo guarda ella.
 */
export function VistaPorFechas({
  estrategias,
  filtro: filtroDeAfuera,
  onCambiarFiltro,
  version = 0,
  puedeCorrer = false,
  puedeReintentar,
  onVolverAIntentar,
  onIrA,
}: {
  estrategias: Array<{ id: string; nombre: string }>;
  filtro?: FiltroDeCorridas;
  onCambiarFiltro?: (f: FiltroDeCorridas) => void;
  version?: number;
  puedeCorrer?: boolean;
  /** Revisión final de rama (F3): si su estrategia puede volver a correr ahora. */
  puedeReintentar?: (estrategiaId: string) => boolean;
  onVolverAIntentar?: (estrategiaId: string) => void;
  onIrA?: (
    destino: DestinoHoy,
    strategyApplicationId: string | null,
    filtro?: FiltroBandeja
  ) => void;
}) {
  const hoy = diaDeBolivia();
  const [filtroPropio, setFiltroPropio] = useState<FiltroDeCorridas>(filtroInicialPorFechas);
  const filtro = filtroDeAfuera ?? filtroPropio;
  const setFiltro = onCambiarFiltro ?? setFiltroPropio;
  const linea = useLineaDeTiempo(filtro, version);
  const [productosDe, setProductosDe] = useState<EventoDeLaLinea | null>(null);
  const nombreDeLaParte = useMemo(
    () => estrategias.find((e) => e.id === filtro.estrategia)?.nombre ?? null,
    [estrategias, filtro.estrategia]
  );

  return (
    <div className="space-y-3" data-testid="vista-por-fechas">
      <FiltrosDeCorridas valor={filtro} onCambiar={setFiltro} estrategias={estrategias} />
      {linea.cargando ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : linea.error ? (
        <EstadoError onReintentar={linea.reintentar}>{linea.error}</EstadoError>
      ) : (
        <>
          {linea.resumen && <NumerosDelPeriodo resumen={linea.resumen} />}
          {linea.eventos.length === 0 ? (
            <p className="rounded-tarjeta border bg-card shadow-sm p-6 text-center text-sm text-muted-foreground">
              No hay corridas con estos filtros.
            </p>
          ) : (
            <LineaDeTiempo
              eventos={linea.eventos}
              hoy={hoy}
              nombreDeLaParte={nombreDeLaParte}
              puedeCorrer={puedeCorrer}
              puedeReintentar={puedeReintentar}
              onVolverAIntentar={onVolverAIntentar}
              onVerProductos={setProductosDe}
            />
          )}
          {linea.errorMas && <Aviso tono="alerta">{linea.errorMas}</Aviso>}
          {linea.haySiguiente ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={linea.cargandoMas}
                onClick={() => void linea.verMas()}
              >
                Ver más
              </Button>
            </div>
          ) : (
            <AvisoAntesDelRegistro registroDesde={linea.registroDesde} periodo={filtro.periodo} />
          )}
        </>
      )}
      <ProductosDeLaCorridaDialog
        evento={productosDe}
        estrategiaDeLaParte={filtro.estrategia}
        nombreDeLaParte={nombreDeLaParte}
        onCerrar={() => setProductosDe(null)}
        onIrA={onIrA}
      />
    </div>
  );
}
